/**
 * record-list.js
 */

import React from 'react';
import {
  useMediaQuery,
  Box,
  Button as MuiButton,
  FormControl,
  FormGroup,
  Grow,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ReactSVG } from 'react-svg';
import { useRouteMatch, useHistory, Link as RouterLink } from 'react-router-dom';
import { format, getMonth, getYear, parseISO } from 'date-fns';
import { Types } from 'utils/constants';
import { getPaginationPaths } from 'utils/routing';
import { getFilterParamsObj } from 'utils/filter-params';
import { UserContext } from 'contexts/user/user-context';
import useRecordStore from 'contexts/record-store';
import useRecordList from 'contexts/record-list';
import RecordMenuContextProvider from 'contexts/record-context-menu/record-menu.provider';
import RecordMenuContext from 'contexts/record-context-menu/record-menu.context';
import { LiveTrackingContext } from 'components/tracking/live-tracking/live-tracking-context';
import { TRACK_TYPE } from 'components/tracking/tracking-common';
import Button from 'components/button';
import FeatureBox from 'components/feature-box';
import Icon from 'components/icon';
import eventSVG from 'assets/images/event.svg';
import EventHeader from './training/event-header';
import ExerciseRow from './training/exercise-row';
import DeploymentHeader from './deployment/deployment-header';
import DeploymentRow from './deployment/deployment-row';
import TrainingClassRow from './training-class/training-class-row';
import TrainingClassHeader from './training-class/training-class-header';
import VetVisitHeader from './vet-visit/vet-visit-header';
import VetVisitRow from './vet-visit/vet-visit-row';
import MoveToEventDialog from './context-menu/context-menu-dialogs/move-to-event-dialog';
import DuplicateDialog from './context-menu/context-menu-dialogs/duplicate-record-dialog';
import DeleteRecordDialog from './context-menu/context-menu-dialogs/delete-record-dialog';
import DeleteCompleteAnswerDialog from './context-menu/context-menu-dialogs/delete-complete-answers-dialog';
import { RecordsContext } from '../records-context';
import ReportDialog from './report-dialog/';
import RecordFilterChips from './record-filter-chips';
import ReportDialogSingle from './report-dialog/report-dialog-single';


const useStyles = makeStyles(theme => ({
  titleBarContainer: {
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(4),
    },
  },
  titleBar: {
    display: 'flex',
    alignItems: 'center',
  },
  titleContainer: {
    flexGrow: 2,
  },
  mainTitle: {
    '& .heading': {
      fontWeight: 600,
    }
  },
  controlsContainer: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    '& > *': {
      marginLeft: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginLeft: theme.spacing(1.5),
      },
    },
  },
  saveSearchButton: {
    minWidth: 'unset',
    '& .MuiSvgIcon-root': {
      fontSize: 20,
      marginTop: 2,
      marginBottom: 2,
    },
  },
  recordsContainer: {
    width: '100%',
  },
  recordRows: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '& .eventSVG svg': {
      display: 'block',
      width: '50%',
      minWidth: 160,
      maxWidth: theme.spacing(40),
      height: 'auto',
      margin: `0px auto ${theme.spacing(3)}`,
      opacity: 0.5,
      '& .custom-fill': {
        fill: theme.palette.primary.colors[1],
      },
    },
    '& > *:first-child > $dateCategory': {
        marginTop: theme.spacing(1),
    }, 
  },
  recordGroup: {
    marginTop: theme.spacing(5),
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(3),
    }
  },
  recordGroupContainer: {
    '&:first-of-type $recordGroup': {
      marginTop: 0,
    },
  },
  dateCategory: {
    textTransform: 'uppercase',
    fontWeight: '500',
    fontSize: theme.typography.sizes[3],
    color: theme.palette.sys.onSurfaceVariant,
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(3),
  },
  emptyList: {
    textAlign: 'center',
    margin: theme.spacing(5),
  },
  emptyHeading: {
    fontWeight: '600',
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.sizes[4],
    },
  },
  emptyText: {
    color: theme.palette.grey.colors[5],
  },
  statusBar: {
    height: 'auto',
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
    '&.top': {
      justifyContent: 'center',
      marginBottom: theme.spacing(2),
      padding: `0 ${theme.spacing(3)}`,
      borderBottom: `1px solid ${theme.palette.grey.colors[8]}`,
      color: theme.palette.grey.colors[5],
      '& .pt-icon': {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.colors[6]
      }
    },
    '&.bottom': {
      marginTop: theme.spacing(3),
      justifyContent: 'space-between',
      '& .pt-icon': {
        fontSize: theme.typography.sizes[4],
      },
      '& > * > *': {
        margin: `0 ${theme.spacing(0.5)}`,
      },
      '& .report-footer': {
        lineHeight: 1.75,
        '&.count': {
          fontWeight: 400,
        }
      },
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'flex-end',
      },
    },
  },
  selectionButton: {
    textTransform: 'none',
    fontSize: theme.typography.sizes[1],
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: 1.5,
    padding: 0,
    marginBottom: theme.spacing(2),
  },
}));


const emptyRecordSelection = {
  [Types.RecordGroup.TRAINING]: [],
  [Types.RecordGroup.DEPLOYMENT]: [],
  [Types.RecordGroup.CLASS]: [],
  [Types.RecordGroup.VET_VISIT]: [],
  count: 0
};


const groupRecords = (records, calendarDate) => {
  const dateCategories = new Map();
  let currentGroupId = '';
  let currentGroup = null;
  const currentDate = new Date();
  const currentMonth = getMonth(currentDate);
  const currentYear = getYear(currentDate);
  for (var i = 0; i < records.length; i++) {
    const record = records[i];
    const groupId = `${record.type}-${record.eventId}`;
    if (groupId !== currentGroupId) {
      currentGroupId = groupId;
      currentGroup = {
        header: {
          type: record.type,
          id: record.eventId
        },
        records: []
      };
      
      const groupDate = parseISO(record.date);
      let groupDateRange;
      if (calendarDate) {
        groupDateRange = format(groupDate, 'MMM d, yyyy');
      } else {
        groupDateRange = groupDate > currentDate 
          ? 'Upcoming' 
          : currentMonth === getMonth(groupDate) && currentYear === getYear(groupDate)
            ? 'This Month' 
            :format(groupDate, 'MMMM yyyy');
      }

      if (dateCategories.has(groupDateRange)) {
        dateCategories.get(groupDateRange).push(currentGroup);
      } else {
        dateCategories.set(groupDateRange, [currentGroup]);
      }
    }
    currentGroup.records.push({...record});
  }
  
  return Array.from(dateCategories).map(([key, val]) => ({ dateCategory: key, groups: val }));
}


// tests if given event and exercise list is relevant to current active track.
// used to determine whether we can delete an event or exercise while actively tracking.
const isTrainingTrackingRelevant = (activeTrack, eventId, exerciseIds) => {
  return (
    // created record is event
    (activeTrack?.createdRecord?.recordType === Types.Record.EVENT && activeTrack?.createdRecord?.id === eventId)
    // created record is exercise
    || (activeTrack?.createdRecord?.recordType === Types.Record.EXERCISE && exerciseIds.includes(activeTrack?.createdRecord?.id))
    // specified record is exercise
    || (exerciseIds.includes(activeTrack?.params?.exerciseId))
  );
}


export default function RecordList(props) {
  const classes = useStyles();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { user } = React.useContext(UserContext);
  const { 
    searchOptions,
    setIsFilterOpen,
    setIsSearchSaveOpen, 
    isReportDialogOpen, 
    setIsReportDialogOpen, 
  } = React.useContext(RecordsContext);

  const history = useHistory();
  const queryParams = history.location.search;
  const { params, url: basePath } = useRouteMatch();
  const filterParamsString = params.filterParamsString;
  const filterParams = React.useMemo(() => {
    return getFilterParamsObj(filterParamsString);
  }, [filterParamsString]);
  const filterMode = params.filterMode || '';
  const isFilterActive = Boolean(filterParamsString);
  const calendarDate = filterParams.date || null;
  const pageNum = parseInt(params.pageNum) || 1;
  const activeSavedSearchId = filterMode === Types.FilterMode.SAVED_SEARCH
    ? +params.filterOption
    : null;
  const activeSavedSearch = activeSavedSearchId 
    ? searchOptions?.savedSearches.find(s => s.id === activeSavedSearchId)
    : null;

  const recordList = useRecordList();
  const recordListState = recordList.useState();
  const records = recordListState.records;
  const numPages = recordListState.pageCount;
  const filteredRecordCount = recordListState.recordCountTotal;
  const recordDates = records.map(r => r.date).join('__');

  const recordStore = useRecordStore();
  const status = recordStore.useStatus();
  const isInitializing = status.isInitializing.get();
  const isLoading = status.isLoading.get() || recordListState.isLoading;
  const recordCount = status.stats.recordCount.get();

  const pagePaths = getPaginationPaths(basePath, pageNum, numPages, queryParams);
  const [isPageNavDisabled, setIsPageNavDisabled] = React.useState(false);
  const [isOtherLoading, setIsOtherLoading] = React.useState(false);
  const handlerDogs = searchOptions?.handlerDogs;
  const supervisors = searchOptions?.supervisors;
  const managedHandlers = searchOptions?.managedHandlers;

  const { activeTrack } = React.useContext(LiveTrackingContext);
 

  // reporting
  const [singleReport, setSingleReport] = React.useState();
  const [selectedRecords, setSelectedRecords] = React.useState(emptyRecordSelection);


  // main title
  const mainTitle = `${isFilterActive
    ? `${filteredRecordCount} out of ${recordCount}`
    : `${recordCount} total`} records`;



  // clear selections if role changes
  React.useEffect(() => {
    setSelectedRecords(emptyRecordSelection);
  }, [user.role.id]);


  // update store with selected dates when record list changes.
  // this could be in record-calendar too. shrug.
  React.useEffect(() => {
    const dates = recordDates ? [...new Set(recordDates.split('__'))] : [];
    recordStore.setSelectedDates(dates);
  // recordStore is a complex object
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordDates]);


  // hackish way to prevent fast page-nav clicks
  const onPageNavClick = () => {
    setIsPageNavDisabled(true);
    setTimeout(() => setIsPageNavDisabled(false), 500);
  }


  // individual checkbox handler
  const onSelectionChange = (type, id, isSelected) => {
    setSelectedRecords(p => ({
      ...p,
      [type]: isSelected 
        ? [...p[type], id]
        : p[type].filter(r => r !== id),
      count: p.count + (isSelected ? 1 : -1)
    }));
  }


  // clear selections
  const onClearSelection = () => {
    setSelectedRecords(emptyRecordSelection);
  }


  const onViewSingleReport = (recordGroup, recordIds) => {
    setSingleReport({ recordGroup, recordIds });
  }


  const renderHeader = (header, records) => {
    switch (header.type) {
      case Types.Record.EVENT:
      case Types.Record.EXERCISE:
        const isRecordTrackingRelevant = isTrainingTrackingRelevant(activeTrack, header.id, records.map(r => r.id));
        return <EventHeader
          id={header.id}
          basePath={basePath}
          isRecordTrackingRelevant={isRecordTrackingRelevant}
        />;

      case Types.Record.DEPLOYMENT:
        return <DeploymentHeader
          id={header.id}
          basePath={basePath}
        />;

      case Types.Record.CLASS:
        return <TrainingClassHeader
          id={header.id}
          basePath={basePath}
        />;        

      case Types.Record.VET_VISIT:
        return <VetVisitHeader
          id={header.id}
          basePath={basePath}
        />;    

      default:
        return null;
    }
  }


  const renderRecord = (record) => {
    const isSelected = selectedRecords[record.groupType].includes(record.id);
    let isRecordTrackingRelevant = false;

    switch(record.type) {
      case Types.Record.EXERCISE:
        isRecordTrackingRelevant = isTrainingTrackingRelevant(activeTrack, record.eventId, [record.id]);
        return <ExerciseRow
          key={`ex-${record.id}`}
          id={record.id}
          basePath={basePath}
          isSelected={isSelected}
          isRecordTrackingRelevant={isRecordTrackingRelevant}
          onSelectionChange={onSelectionChange}
          onViewReport={onViewSingleReport}
        />;

      case Types.Record.DEPLOYMENT:
        isRecordTrackingRelevant = (
          activeTrack?.type === TRACK_TYPE.DEPLOYMENT
          && activeTrack?.deployment?.id === record.id
        );
        return <DeploymentRow
          key={`dep-${record.id}`}
          id={record.id}
          basePath={basePath}
          isSelected={isSelected}
          onSelectionChange={onSelectionChange}
          onViewReport={onViewSingleReport}
          isRecordTrackingRelevant={isRecordTrackingRelevant}
        />;

      case Types.Record.CLASS:
        return <TrainingClassRow
          key={`class-${record.id}`}
          id={record.id}
          basePath={basePath}
          isSelected={isSelected}
          onSelectionChange={onSelectionChange}
          onViewReport={onViewSingleReport}
        />;

      case Types.Record.VET_VISIT:
        return <VetVisitRow
          key={`class-${record.id}`}
          id={record.id}
          basePath={basePath}
          isSelected={isSelected}
          onSelectionChange={onSelectionChange}
          onViewReport={onViewSingleReport}
        />;

      default:
        return null;
    }
  };


  return <>
    <RecordMenuContextProvider>
      <div id="records-list" data-testid="records-list">
        <FeatureBox
          loading={!isInitializing && (isLoading || isOtherLoading)}
          message={'Loading records ...'}
          style={{ minHeight: 140 }}
        >
          {/* title & report button */}
          {!isLoading && filteredRecordCount !== -1 && 
            <div id="records-list-header">
              {(isFilterActive || !isMobile) &&
                <div className={classes.titleBarContainer} >
                  <div className={classes.titleBar} >

                    {/* TITLE */}
                    <div className={classes.titleContainer}>
                      {isMobile
                        ? isFilterActive && <>
                          <Typography variant='body1' component='div' className={classes.mainTitle}>
                            <div className='heading'>Filter Active</div>
                            <div>
                              {filteredRecordCount} / {recordCount} Records
                            </div>
                          </Typography>
                        </>
                        : <Typography variant='body1' component='div' className={classes.mainTitle}>
                          {mainTitle}
                        </Typography>

                      }
                    </div>

                    {/* CONTROLS */}
                    <div className={classes.controlsContainer}>
                      {isFilterActive && isMobile &&
                      <MuiButton
                        component={RouterLink}
                        to={`/records`}
                        variant='outlined'
                        color='primary'
                        startIcon={<Icon type='filter-remove' />}
                        children='Clear'
                      />}
                      {isFilterActive && !activeSavedSearch &&
                      <Tooltip title='Click here to save this search for next time'>
                        <MuiButton
                          className={classes.saveSearchButton}
                          color='primary'
                          variant={isMobile ? 'outlined' : undefined}
                          startIcon={isMobile ? undefined : <Icon type='bookmark' />}
                          onClick={() => setIsSearchSaveOpen(true)}
                          children={isMobile ? <Icon type='bookmark' /> : 'Save'}
                        />
                      </Tooltip>}
                      {!isMobile &&
                      <Tooltip title='Filter the list below by record type, dog, date range or other criteria'>
                        <Button
                          data-testid='records-filter'
                          onClick={() => setIsFilterOpen(true)}
                          startIcon={<Icon type="custom" />}
                          variant='outlined'
                          children='Filter'
                        />
                      </Tooltip>}
                    </div>
                  </div>
                  <RecordFilterChips
                    searchOptions={searchOptions}
                    showClear={!isMobile}
                  />
                </div>
              }
            </div>
          }

          <div className={classes.recordsContainer}>

            {/* CLEAR SELECTION */}
            {!isMobile && selectedRecords.count > 0 &&
              <MuiButton
                color='primary'
                className={classes.selectionButton}
                onClick={onClearSelection}
                children={<>&times; CLEAR {selectedRecords.count} SELECTED RECORD{selectedRecords.count === 1 ? '' : 'S'}</>}
              />
            }   

            {/* top status bar */}
            {recordListState.possiblyOutdated &&
              <Grow in={true}>
                <div className={`flex-hc ${classes.statusBar} top`}>
                  <Icon type='info' />
                  Results may be outdated.
                  <MuiButton
                    style={{ marginLeft: 8 }}
                    onClick={() => recordStore.refreshRecords()}
                  >
                    Refresh
                  </MuiButton>
                </div>
              </Grow>
            }

            {/* records */}
            <FormControl component="fieldset" style={{ width: '100%' }}>
              <FormGroup>
                <div className={classes.recordRows}>

                  {/* empty list */}
                  {records.length === 0 && !isLoading &&
                    <Grow in={true} timeout={1000}>
                      <div className={classes.emptyList}>
                        <ReactSVG src={eventSVG} className="eventSVG" />
                        <Typography variant="h6" className={classes.emptyHeading}>
                          No records found
                        </Typography>
                        <Typography variant="body2" className={classes.emptyText}>
                          {user.role.id !== Types.Role.SUPERVISOR && user.role.id !== Types.Role.TRAINER &&
                            <>
                              Add training or deployment records<br />
                              using the {isMobile
                                ? <><strong>+</strong> (plus) button in the upper right</>
                                : <><strong>+ Add Record</strong> button above</>
                              }.
                            </>
                          }
                          {(user.role.id === Types.Role.SUPERVISOR || user.role.id === Types.Role.TRAINER) &&
                            <>
                              The records of handlers that you {user.role.id === Types.Role.SUPERVISOR ? 'supervise' : 'train'} will be shown here.<br />
                              Invite handlers to be {user.role.id === Types.Role.SUPERVISOR ? 'supervised' : 'trained'} on the <strong>Manage</strong> page.
                            </>
                          }
                        </Typography>
                      </div>
                    </Grow>}

                  {/* records list */}
                  {groupRecords(records, calendarDate).map(({ dateCategory, groups }) => (
                    <div key={`${pageNum}-${dateCategory}`}>
                      <Typography variant="body1" className={classes.dateCategory}>{dateCategory}</Typography>
                      {groups.map(group => (
                        <div
                          key={`${pageNum}-${group.header.type}-${group.header.id}`}
                          className={classes.recordGroupContainer}
                        >
                          <Grow in={true} timeout={500}>
                            <div className={`${classes.recordGroup} element-default-box-shadow`}>
                              {renderHeader(group.header, group.records)}
                              {group.records.map(record => renderRecord(record))}
                            </div>
                          </Grow>
                        </div>
                      ))}
                    </div>
                  ))}

                </div>
              </FormGroup>
            </FormControl>
          </div>

          {/* bottom status bar */}
          {records.length > 0 &&
          <div className={`flex-hc ${classes.statusBar} bottom`} data-testid="record-footer-links">
            <Box flexGrow={1}>
              <div className='flex-hc' style={{ justifyContent: 'flex-end' }}>
                <IconButton
                  component={RouterLink}
                  to={pagePaths.prev || ''}
                  disabled={!pagePaths.prev || isPageNavDisabled}
                  onClick={onPageNavClick}
                  data-testid="prev-page"
                  children={<Icon type='nav-back' />}
                  size="large" />
                <Typography variant='body2'>
                  Page {pageNum} / {numPages}
                </Typography>
                <IconButton
                  component={RouterLink}
                  to={pagePaths.next || ''}
                  disabled={!pagePaths.next || isPageNavDisabled}
                  onClick={onPageNavClick}
                  data-testid="next-page"
                  children={<Icon type='nav-forward' />}
                  size="large" />
              </div>
            </Box>
          </div>}
          
        </FeatureBox>
      </div>

      <RecordMenuContext.Consumer>
        {(actionState) => {
          if (actionState.action.type !== '') {
            setIsOtherLoading(actionState.isLoading);
          }

          switch (actionState.action.type) {
            case 'MOVE_TO_EVENT':
              return <MoveToEventDialog
                {...actionState.action.params}
                onClose={actionState.closeAction}
                setIsLoading={actionState.setIsLoading} 
              />;

            case 'DUPLICATE_EVENT':
            case 'DUPLICATE_EXERCISE':
              return <DuplicateDialog
                {...actionState.action.params}
                onClose={actionState.closeAction}
                setIsLoading={actionState.setIsLoading} 
              />;

            case 'DELETE_EVENT':
            case 'DELETE_EXERCISE':
            case 'DELETE_DEPLOYMENT':
            case 'DELETE_CLASS':
            case 'DELETE_VET_VISIT':
              return <DeleteRecordDialog
                {...actionState.action.params}
                onClose={actionState.closeAction}
                setIsLoading={actionState.setIsLoading} 
              />;

            case 'DELETE_COMPLETED_ANSWERS':
              return <DeleteCompleteAnswerDialog
                {...actionState.action.params}
                onClose={actionState.closeAction}
                setIsLoading={actionState.setIsLoading} 
              />;

            default:
              return;
          }
        }}
      </RecordMenuContext.Consumer>

    </RecordMenuContextProvider>

    <ReportDialog
      open={isReportDialogOpen}
      onClose={() => setIsReportDialogOpen(false)}
      filterParams={filterParams}
      isFilterActive={isFilterActive}
      handlerDogs={handlerDogs}
      supervisors={supervisors}
      managedHandlers={managedHandlers}
      searchedRecordType={+filterParams?.recordGroups?.[0] || null}
      selectedRecords={selectedRecords}
      recordCount={recordCount}
      filteredRecordCount={isFilterActive ? filteredRecordCount : 0}
      selectedRecordCount={selectedRecords.count}
    />

    <ReportDialogSingle
      recordGroup={singleReport?.recordGroup}
      recordIds={singleReport?.recordIds}
      onClose={() => setSingleReport()}
    />
  </>;
}